// Creazione nuovo ticket con wizard a step
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronRight } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { DatePicker } from '../../components/ui/DatePicker'
import { Alert } from '../../components/ui/Alert'
import { MapPicker } from '../../components/shared/MapPicker'
import { FileUpload } from '../../components/shared/FileUpload'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import { useCreateTicket } from '../../hooks/useTickets'
import api from '../../lib/api'
// Coords locali per la mappa
interface Coords { lat: number; lng: number }

const schema = z.object({
  title: z.string().min(5, 'Titolo troppo corto').max(200),
  description: z.string().optional(),
  type: z.enum(['fault', 'scheduled_maintenance', 'inspection', 'installation', 'testing', 'urgent']),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  companyId: z.string().min(1, 'Seleziona un cliente'),
  siteId: z.string().optional(),
  scheduledStart: z.string().optional(),
  internalNotes: z.string().optional(),
})

type FormData = z.infer<typeof schema>

const STEPS = ['Informazioni', 'Localizzazione', 'Allegati', 'Riepilogo']

export function NewTicketPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [location, setLocation] = useState<Coords | null>(null)
  const [files, setFiles] = useState<Array<{ file: File; preview?: string }>>([])
  const [masterCompanyId, setMasterCompanyId] = useState('')
  const [subcontractorId, setSubcontractorId] = useState('')
  const createMutation = useCreateTicket()

  const { data: companies = [] } = useQuery({
    queryKey: ['companies', 'options'],
    queryFn: async () => {
      const res = await api.get<Array<{ id: string; name: string }>>('/companies')
      return res.data
    },
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      priority: 'medium',
      type: 'fault',
    },
  })

  const siteId = watch('siteId')
  const [ticketCategory, setTicketCategory] = useState<'standard' | 'maintenance'>('standard')
  const [selectedChecklistId, setSelectedChecklistId] = useState('')

  const { data: subcontractorList = [] } = useQuery({
    queryKey: ['subcontractors', masterCompanyId],
    queryFn: async () => {
      if (!masterCompanyId) return []
      try {
        const res = await api.get<Array<{ id: string; name: string }>>(`/companies/${masterCompanyId}/subcontractors`)
        return res.data ?? []
      } catch { return [] }
    },
    enabled: Boolean(masterCompanyId),
  })

  const effectiveCompanyId = subcontractorId || masterCompanyId

  const { data: sites = [] } = useQuery({
    queryKey: ['sites', effectiveCompanyId],
    queryFn: async () => {
      try {
        const res = await api.get<Array<{ id: string; name: string; lat?: number; lng?: number; latitude?: number; longitude?: number }>>('/sites', { params: { companyId: effectiveCompanyId } })
        return res.data ?? []
      } catch {
        return []
      }
    },
    enabled: Boolean(effectiveCompanyId),
  })

  const { data: siteContacts = [] } = useQuery({
    queryKey: ['site-contacts', siteId],
    queryFn: async () => {
      if (!siteId) return []
      try {
        const res = await api.get<Array<{ id: string; name: string; role?: string; phone?: string; email?: string; isPrimary?: boolean }>>(`/sites/${siteId}/contacts`)
        return res.data ?? []
      } catch { return [] }
    },
    enabled: Boolean(siteId),
  })

  const { data: selectedCompany } = useQuery({
    queryKey: ['company-geo', masterCompanyId],
    queryFn: async () => {
      const res = await api.get<{ notes?: string; address?: string; city?: string }>(`/companies/${masterCompanyId}`)
      return res.data
    },
    enabled: Boolean(masterCompanyId),
  })

  // Centro mappa: priorità sito selezionato → company GPS → Roma fallback
  const mapCenter = (() => {
    if (siteId) {
      const site = sites.find((s) => s.id === siteId)
      const lat = site?.lat ?? site?.latitude
      const lng = site?.lng ?? site?.longitude
      if (lat && lng) return { lat, lng }
    }
    if (selectedCompany?.notes) {
      try {
        const geo = JSON.parse(selectedCompany.notes) as { lat?: number; lng?: number }
        if (geo.lat && geo.lng) return { lat: Number(geo.lat), lng: Number(geo.lng) }
      } catch { /* niente */ }
    }
    return undefined // usa default Roma del MapPicker
  })()

  const { data: checklistTemplates = [] } = useQuery({
    queryKey: ['checklist-templates', effectiveCompanyId, siteId],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (effectiveCompanyId) params['customerId'] = effectiveCompanyId
      if (siteId) params['siteId'] = siteId
      return (await api.get<{ id: string; name: string; operations: { id: string }[] }[]>('/checklist-templates', { params })).data
    },
    enabled: ticketCategory === 'maintenance',
  })

  const nextStep = async () => {
    const fields = step === 0 ? ['title', 'description', 'type', 'priority', 'companyId'] : []
    const valid = fields.length === 0 || await trigger(fields as Parameters<typeof trigger>[0])
    if (valid) setStep((s) => s + 1)
  }

  const onSubmit = async (data: FormData) => {
    const formData = new FormData()
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined) formData.append(k, v)
    })
    if (location) {
      formData.append('lat', String(location.lat))
      formData.append('lng', String(location.lng))
    }
    files.forEach(({ file }) => formData.append('files', file))

    try {
      const ticket = await createMutation.mutateAsync({
        ...data,
        ticketCategory,
        ...(ticketCategory === 'maintenance' && selectedChecklistId
          ? { maintenanceChecklistId: selectedChecklistId }
          : {}),
      } as any)

      if (ticketCategory === 'maintenance' && selectedChecklistId && ticket?.id) {
        await api.post('/maintenance/executions', {
          ticketId: ticket.id,
          templateId: selectedChecklistId,
        })
      }

      // Carica allegati se presenti
      if (files.length > 0 && ticket?.id) {
        const fd = new FormData()
        files.forEach(({ file }) => fd.append('files', file))
        await api.post(`/tickets/${ticket.id}/attachments`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }

      navigate(`/tickets/${ticket.id}`)
    } catch {
      // Errore gestito dallo stato mutation
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <PageHeader
        title="Nuovo ticket"
        breadcrumb={[{ label: 'Ticket', href: '/tickets' }, { label: 'Nuovo' }]}
      />

      {/* Progress steps */}
      <nav aria-label="Step wizard">
        <ol className="flex items-center gap-2">
          {STEPS.map((label, index) => (
            <li key={label} className="flex items-center gap-2">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold
                ${index < step ? 'bg-[var(--fc-success)] text-white'
                  : index === step ? 'bg-[var(--fc-primary)] text-white'
                  : 'bg-[var(--fc-gray-200)] text-[var(--fc-foreground-muted)]'}`}
                aria-current={index === step ? 'step' : undefined}
              >
                {index < step ? <Check className="h-4 w-4" /> : index + 1}
              </div>
              <span className={`hidden sm:block text-sm ${index === step ? 'font-semibold text-[var(--fc-foreground)]' : 'text-[var(--fc-foreground-muted)]'}`}>
                {label}
              </span>
              {index < STEPS.length - 1 && (
                <ChevronRight className="h-4 w-4 text-[var(--fc-gray-300)]" aria-hidden="true" />
              )}
            </li>
          ))}
        </ol>
      </nav>

      {createMutation.error && (
        <Alert variant="error">Errore durante la creazione del ticket. Riprova.</Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Card>
          {/* Step 0: Informazioni */}
          {step === 0 && (
            <div className="space-y-4">
              <Input
                label="Titolo"
                required
                placeholder="Descrizione breve dell'intervento"
                error={errors.title?.message}
                {...register('title')}
              />

              <div className="grid grid-cols-2 gap-4">
                <Select
                  label="Tipo intervento"
                  required
                  options={[
                    { value: 'fault', label: 'Guasto' },
                    { value: 'scheduled_maintenance', label: 'Manutenzione programmata' },
                    { value: 'installation', label: 'Installazione' },
                    { value: 'inspection', label: 'Ispezione' },
                    { value: 'testing', label: 'Test' },
                    { value: 'urgent', label: 'Urgente' },
                  ]}
                  value={watch('type')}
                  onValueChange={(v) => setValue('type', v as FormData['type'])}
                  error={errors.type?.message}
                />
                <Select
                  label="Priorità"
                  required
                  options={[
                    { value: 'low', label: 'Bassa' },
                    { value: 'medium', label: 'Media' },
                    { value: 'high', label: 'Alta' },
                    { value: 'critical', label: 'Critica' },
                  ]}
                  value={watch('priority')}
                  onValueChange={(v) => setValue('priority', v as FormData['priority'])}
                  error={errors.priority?.message}
                />
              </div>

              <Select
                label="Cliente"
                required
                options={companies.map((c) => ({ value: c.id, label: c.name }))}
                value={masterCompanyId}
                onValueChange={(v) => {
                  setMasterCompanyId(v)
                  setSubcontractorId('')
                  setValue('siteId', undefined)
                  setValue('companyId', v)
                }}
                error={errors.companyId?.message}
              />

              {subcontractorList.length > 0 && (
                <div className="rounded-lg border border-[var(--fc-border)] bg-[color-mix(in_srgb,var(--fc-primary)_5%,transparent)] p-3 space-y-2">
                  <p className="text-xs font-medium text-[var(--fc-primary)]">
                    Questo cliente ha clienti in subappalto
                  </p>
                  <Select
                    label="Subappaltato *"
                    required
                    options={[
                      { value: '__master', label: `— Nessun subappalto (${companies.find(c => c.id === masterCompanyId)?.name ?? ''}) —` },
                      ...subcontractorList.map((s) => ({ value: s.id, label: s.name })),
                    ]}
                    value={subcontractorId || '__master'}
                    onValueChange={(v) => {
                      if (v === '__master') {
                        setSubcontractorId('')
                        setValue('companyId', masterCompanyId)
                      } else {
                        setSubcontractorId(v)
                        setValue('companyId', v)
                      }
                      setValue('siteId', undefined)
                    }}
                  />
                </div>
              )}

              {sites.length > 0 && (
                <Select
                  label="Sede"
                  options={[{ value: 'none', label: 'Nessuna sede' }, ...sites.map((s) => ({ value: s.id, label: s.name }))]}
                  value={watch('siteId') ?? 'none'}
                  onValueChange={(v) => {
                    if (v === 'none') {
                      setValue('siteId', undefined)
                    } else {
                      setValue('siteId', v)
                      // Auto-posiziona mappa con coordinate della sede
                      const site = sites.find((s) => s.id === v)
                      const lat = parseFloat(String(site?.lat ?? site?.latitude ?? ''))
                      const lng = parseFloat(String(site?.lng ?? site?.longitude ?? ''))
                      if (!isNaN(lat) && !isNaN(lng)) setLocation({ lat, lng })
                    }
                  }}
                />
              )}

              {siteContacts.length > 0 && (
                <div className="rounded border border-[var(--fc-border)] bg-[var(--fc-surface-overlay)] p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-[var(--fc-foreground-muted)] uppercase tracking-wide">
                    Referenti sede
                  </p>
                  {siteContacts.map((contact) => (
                    <div key={contact.id} className="flex items-center gap-3 text-xs text-[var(--fc-foreground)]">
                      <span className="font-medium">{contact.name}</span>
                      {contact.role && <span className="text-[var(--fc-foreground-muted)]">{contact.role}</span>}
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="text-[var(--fc-primary)] hover:underline">{contact.phone}</a>
                      )}
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="text-[var(--fc-primary)] hover:underline">{contact.email}</a>
                      )}
                      {contact.isPrimary && (
                        <span className="rounded bg-[color-mix(in_srgb,var(--fc-primary)_15%,transparent)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--fc-primary)]">
                          Principale
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Categoria ticket */}
              <div>
                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">
                  Categoria intervento
                </label>
                <div className="flex gap-3">
                  {(['standard', 'maintenance'] as const).map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => { setTicketCategory(cat); setSelectedChecklistId('') }}
                      className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                        ticketCategory === cat
                          ? 'border-[var(--fc-primary)] bg-blue-50 text-[var(--fc-primary)]'
                          : 'border-[var(--fc-border)] text-[var(--fc-foreground-muted)]'
                      }`}
                    >
                      {cat === 'standard' ? 'Standard' : 'Manutenzione'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Checklist selector (solo manutenzione) */}
              {ticketCategory === 'maintenance' && (
                <div>
                  <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">
                    Checklist manutenzione
                  </label>
                  <select
                    value={selectedChecklistId}
                    onChange={e => setSelectedChecklistId(e.target.value)}
                    className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                  >
                    <option value="">— seleziona template —</option>
                    {checklistTemplates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.operations?.length ?? 0} op.)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
                  Descrizione
                </label>
                <textarea
                  rows={4}
                  placeholder="Descrizione dettagliata dell'intervento..."
                  className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-subtle)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)] resize-none"
                  {...register('description')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--fc-foreground)] mb-1">
                  Device Guasto
                </label>
                <textarea
                  rows={3}
                  placeholder="Device/apparato guasto e note (es. modello, seriale, sintomo)..."
                  className="w-full rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-subtle)] focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)] resize-none"
                  {...register('internalNotes')}
                />
              </div>

              <DatePicker
                label="Data pianificata"
                {...register('scheduledStart')}
              />
            </div>
          )}

          {/* Step 1: Localizzazione */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--fc-foreground-muted)]">
                Seleziona la posizione dell'intervento sulla mappa (opzionale)
              </p>
              <MapPicker
                value={location}
                onChange={setLocation}
                height="350px"
                center={mapCenter}
                zoom={mapCenter ? 14 : 6}
              />
            </div>
          )}

          {/* Step 2: Allegati */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-[var(--fc-foreground-muted)]">
                Carica documentazione, foto o file relativi all'intervento
              </p>
              <FileUpload
                files={files}
                onAdd={(newFiles) => setFiles((prev) => [...prev, ...newFiles])}
                onRemove={(index) => setFiles((prev) => prev.filter((_, i) => i !== index))}
              />
            </div>
          )}

          {/* Step 3: Riepilogo */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-[var(--fc-foreground)]">Riepilogo ticket</h3>
              <div className="divide-y divide-[var(--fc-border)] rounded-lg border border-[var(--fc-border)]">
                {[
                  { label: 'Titolo', value: watch('title') },
                  { label: 'Tipo', value: watch('type') },
                  { label: 'Priorità', value: watch('priority') },
                  { label: 'Cliente', value: companies.find(c => c.id === masterCompanyId)?.name ?? '' },
                  { label: 'Subappaltato', value: subcontractorList.find(s => s.id === subcontractorId)?.name ?? 'Nessuno' },
                  { label: 'Data pianificata', value: watch('scheduledStart') ?? 'Non specificata' },
                  { label: 'Allegati', value: `${files.length} file` },
                  { label: 'Posizione', value: location ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` : 'Non specificata' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-[var(--fc-foreground-muted)]">{label}</span>
                    <span className="font-medium text-[var(--fc-foreground)]">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Navigazione step */}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => step === 0 ? navigate('/tickets') : setStep((s) => s - 1)}
          >
            {step === 0 ? 'Annulla' : 'Indietro'}
          </Button>

          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={nextStep}>
              Avanti
            </Button>
          ) : (
            <Button type="submit" isLoading={createMutation.isPending}>
              Crea ticket
            </Button>
          )}
        </div>
      </form>

      <ContextualHelp />
    </div>
  )
}
